import React, { useCallback } from 'react';

import { Slider } from '../../../../../Slider/Slider';
import { Asset } from '../../../../../Slider/items/Asset/Asset';
import { Category } from '../../../../../Slider/items/Category/Category';
import { Profile } from '../../../../../Slider/items/Profile/Profile';
import { Story } from '../../../../../Slider/items/Story/Story';
import { useEndpointItems } from '../../../../../../hooks/useEndpointItems';

import type { FC } from 'react';
import type {
  EnrichedTag,
  SvpAsset,
  SvpCategory,
} from '../../../../../../types';
import type { CurateFetchParams } from '../../../../../../services/curate/requestBlend';
import type { SvpFetchParams } from '../../../../../../services/svp/shared/requestSVP';

type EndpointItemsSliderProps =
  | {
      endpoint: 'svp/assets' | 'svp/most-seen' | 'svp/search';
      params: SvpFetchParams;
      dataType: 'assets' | 'stories';
    }
  | {
      endpoint: 'svp/categories';
      params: Record<string, string>;
      dataType: 'categories';
    }
  | {
      endpoint: 'curate/blend';
      params: CurateFetchParams;
      dataType: 'assets';
    }
  | {
      endpoint?: never;
      params: { categoryId?: number };
      dataType: 'profiles';
    };

export const EndpointItemsSlider: FC<EndpointItemsSliderProps> = ({
  endpoint,
  params,
  dataType,
}) => {
  const { data: items = [], isLoading } = useEndpointItems({
    endpoint,
    params,
    dataType,
  } as Parameters<typeof useEndpointItems>[0]);

  const renderItem = useCallback(
    (item: SvpAsset | SvpCategory | EnrichedTag) => {
      if (dataType === 'assets') {
        return <Asset asset={item} />;
      }

      if (dataType === 'stories') {
        return <Story asset={item} />;
      }

      if (dataType === 'categories') {
        return <Category category={item as SvpCategory} />;
      }

      if (dataType === 'profiles') {
        return <Profile profile={item} />;
      }
    },
    [dataType]
  );

  return (
    <Slider
      items={items}
      renderItem={renderItem}
      isFetching={isLoading}
      isSmall={dataType === 'profiles'}
    />
  );
};
